import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery, useMutation } from "react-query";
import { toast } from "react-toastify";
import ReqDataGrid from "./ResultDataGrid";
import CustomPagination from "Shared/JsxPagination";
import useLang from "Shared/hooks/useLanguage";
import usePagination from "Shared/hooks/usePagination";
import RequisitionType from "Services/Requisition/RequisitionTypeService";
import {
  GetAll,
  CreateBatch as CreateBatchApi,
} from "Services/Oncology/oncologyService";
import { AutocompleteStyle } from "Utils/MuiStyles/AutocompleteStyles";
import BreadCrumbs from "Utils/Common/Breadcrumb";
import { convertRangeFiltersToUtc } from "Utils/Common/LabTimeZone";

const TabSelected = styled(Tab)(AutocompleteStyle());

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const getInitialFilterData = (tabId = 1) => ({
  tabId,
  pageSize: 50,
  pageNumber: 1,
  sortColumn: "",
  sortDirection: "",
  filters: [] as any[],
});

const CreateBatchPage = () => {
  const { t } = useLang();
  const labTimeZone = useSelector(
    (state: any) => state?.Reducer?.selectedTenantInfo?.labTimeZone
  );

  const [value, setValue] = useState(0);
  const [gridHeaders, setGridHeaders] = useState<any[]>([]);
  const [filterData, setFilterData] = useState<any>(getInitialFilterData);
  const [searchValue, setSearchValue] = useState<any>({});

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [batchName, setBatchName] = useState("");

  const [fetchKey, setFetchKey] = useState(0);
  const triggerFetch = useCallback(() => setFetchKey((k) => k + 1), []);

  const {
    curPage,
    pageSize,
    setTotal,
    nextPage,
    showPage,
    prevPage,
    setCurPage,
    totalPages,
    pageNumbers,
    setPageSize,
  } = usePagination();

  const searchedTags = useMemo(() => {
    const labels = new Set<string>();
    filterData.filters?.forEach((f: any) => {
      if (f.filterValue) labels.add(f.label);
    });
    return Array.from(labels);
  }, [filterData.filters]);

  const buildSearchParams = () => ({
    tabId: filterData?.tabId,
    pageNumber: filterData?.pageNumber,
    pageSize: filterData?.pageSize,
    sortColumn: filterData.sortColumn,
    sortDirection: filterData.sortDirection,
    // The picked lab dates stay in filterData (the date boxes read them); only
    // the request carries the UTC dates.
    filters: convertRangeFiltersToUtc(
      filterData.filters?.map((filter: any) => ({
        ...filter,
        filterValue:
          typeof filter.filterValue === "string"
            ? filter.filterValue.trim()
            : filter.filterValue,
      })),
      labTimeZone
    ),
  });

  const { data: gridResponse, isLoading: loading } = useQuery(
    ["createBatchGetAll", fetchKey],
    () => GetAll(buildSearchParams()),
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  const gridData = gridResponse?.data;
  const gridTotal = gridData?.total ?? 0;

  const createBatchMutation = useMutation(
    (payload: any) => CreateBatchApi(payload),
    {
      onSuccess: (response) => {
        if (response?.data?.statusCode === 200 || response?.status === 200) {
          toast.success(
            t(response?.data?.message || "Batch created successfully")
          );
          setBatchName("");
          setSelectedRows([]);
          triggerFetch();
        } else {
          toast.error(t(response?.data?.message || "Failed to create batch"));
        }
      },
      onError: (error: any) => {
        toast.error(
          t(
            error?.response?.data?.message ||
              "An error occurred while creating batch"
          )
        );
      },
    }
  );

  useEffect(() => {
    setTotal(gridTotal);
  }, [gridTotal]);

  useEffect(() => {
    setSelectedRows([]);

    const needsUpdate =
      filterData.pageNumber !== curPage || filterData.pageSize !== pageSize;
    if (!needsUpdate) return;

    setFilterData((prev: any) => ({
      ...prev,
      pageNumber: curPage,
      pageSize: pageSize,
    }));
    triggerFetch();
  }, [curPage, pageSize]);

  useEffect(() => {
    const loadTabs = async () => {
      try {
        const response = await RequisitionType.viewRequisitionTabs();
        const tabs = response?.data?.data?.sort(
          (a: any, b: any) => a.sortOrder - b.sortOrder
        );
        setGridHeaders(tabs || []);
      } catch (error) {
        console.error(error);
      }
    };
    loadTabs();
  }, []);

  const resetSearch = () => {
    setFilterData((prev: any) => getInitialFilterData(prev.tabId));
    setCurPage(1);
    setSearchValue({});
    triggerFetch();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const Id: any = event.currentTarget.id;
    const thenum: any = Id.match(/\d+/)[0];
    if (filterData?.tabId !== parseInt(thenum)) {
      setSearchValue({});
      setCurPage(1);
      setSelectedRows([]);

      setFilterData((prev: any) => ({
        ...prev,
        tabId: parseInt(thenum),
        filters: [],
        pageNumber: 1,
      }));
      triggerFetch();
    }
    setValue(newValue);
  };

  const handleTagRemoval = (clickedTag: string) => {
    const remaining = filterData.filters.filter(
      (tab: any) => tab.label !== clickedTag
    );

    if (remaining.length === 0) {
      resetSearch();
      return;
    }

    const removed = filterData.filters.find(
      (tab: any) => tab.label === clickedTag
    );
    setFilterData((prev: any) => ({
      ...prev,
      filters: remaining,
    }));
    setSearchValue((prev: any) => ({
      ...prev,
      [removed.columnKey]: "",
    }));
  };

  const handleRowCheck = (checked: boolean, row: any) => {
    if (checked) {
      setSelectedRows((prev) => [
        ...prev,
        {
          requisitionOrderId: row.RequisitionOrderID,
          accessionNo: row.AccessionNumber,
          panelId: row.PanelID,
        },
      ]);
    } else {
      setSelectedRows((prev) =>
        prev.filter(
          (item) => item.requisitionOrderId !== row.RequisitionOrderID
        )
      );
    }
  };

  const handleAllCheck = (checked: boolean) => {
    if (checked && gridData?.data?.length) {
      setSelectedRows(
        gridData.data.map((row: any) => ({
          requisitionOrderId: row.RequisitionOrderID,
          accessionNo: row.AccessionNumber,
          panelId: row.PanelID,
        }))
      );
    } else {
      setSelectedRows([]);
    }
  };

  const handleSaveAndCreateBatch = () => {
    if (!batchName.trim()) {
      toast.error(t("Batch name is required"));
      return;
    }
    if (selectedRows.length === 0) {
      toast.error(t("Please select at least one record"));
      return;
    }

    createBatchMutation.mutate({
      batchName: batchName.trim(),
      orders: selectedRows,
    });
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div className="app-toolbar py-3 py-lg-3">
        <div className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center">
          <BreadCrumbs />
        </div>
      </div>
      <div className="d-flex flex-column flex-column-fluid">
        <div className="app-content flex-column-fluid">
          <div className="app-container container-fluid">
            <Tabs
              value={value}
              onChange={handleTabChange}
              TabIndicatorProps={{ style: { background: "transparent" } }}
              className="min-h-auto"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-scrollButtons": {
                  width: 0,
                  transition: "width 0.7s ease",
                  "&:not(.Mui-disabled)": { width: "48px" },
                },
              }}
            >
              {Array.isArray(gridHeaders) &&
                gridHeaders.map((items: any) => (
                  <TabSelected
                    key={items.tabID}
                    label={t(items.tabName)}
                    {...a11yProps(items.tabID)}
                    className="fw-bold text-capitalize"
                    data-test-id={items?.tabName?.replace(/\s/g, "")}
                    disabled={loading}
                  />
                ))}
            </Tabs>
            <div className="card tab-content-card">
              <div className="card-body py-2">
                <div className="d-flex gap-2 flex-wrap">
                  {searchedTags.map((tag, index: number) => (
                    <div
                      className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
                      onClick={() => handleTagRemoval(tag)}
                      key={tag + index}
                    >
                      <span className="fw-bold">{tag}</span>
                      <i className="bi bi-x"></i>
                    </div>
                  ))}
                </div>
                <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mb-2 col-12 responsive-flexed-actions">
                  <div className="d-flex gap-2 responsive-flexed-actions">
                    <div className="d-flex align-items-center">
                      <span className="fw-400 mr-3">{t("Records")}</span>
                      <select
                        id="CreateBatchRecords"
                        className="form-select w-125px h-33px rounded py-2"
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          setPageSize(val);
                          setFilterData((prev: any) => ({
                            ...prev,
                            pageSize: val,
                            pageNumber: 1,
                          }));
                          setCurPage(1);
                          triggerFetch();
                        }}
                        value={filterData.pageSize}
                      >
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="150">150</option>
                        <option value="200">200</option>
                      </select>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2 gap-lg-3">
                    <button
                      id="CreateBatchSearch"
                      onClick={() => {
                        setFilterData((prev: any) => ({
                          ...prev,
                          pageNumber: 1,
                        }));
                        setCurPage(1);
                        triggerFetch();
                      }}
                      className="btn btn-linkedin btn-sm fw-500"
                    >
                      {t("Search")}
                    </button>
                    <button
                      onClick={resetSearch}
                      type="button"
                      className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
                      id="CreateBatchReset"
                    >
                      <span>
                        <span>{t("Reset")}</span>
                      </span>
                    </button>
                  </div>
                </div>
                <TabPanel value={value} index={value}>
                  <ReqDataGrid
                    tabsInfo={gridHeaders[value]?.tabHeaders}
                    labTimeZone={labTimeZone}
                    rowInfo={gridData?.data}
                    value={value}
                    loading={loading}
                    filterData={filterData}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    setFilterData={setFilterData}
                    setCurPage={setCurPage}
                    triggerFetch={triggerFetch}
                    selectedRows={selectedRows}
                    onRowCheck={handleRowCheck}
                    onAllCheck={handleAllCheck}
                  />
                </TabPanel>
                {loading ? null : (
                  <CustomPagination
                    curPage={curPage}
                    nextPage={nextPage}
                    pageNumbers={pageNumbers}
                    pageSize={pageSize}
                    prevPage={prevPage}
                    showPage={showPage}
                    total={gridTotal}
                    totalPages={totalPages}
                  />
                )}
                <div className="d-flex align-items-center gap-3 mt-3 mb-2">
                  <input
                    type="text"
                    className="form-control w-250px h-38px"
                    placeholder={t("Enter batch name")}
                    value={batchName}
                    onChange={(e) => setBatchName(e.target.value)}
                  />
                  <button
                    className="btn btn-sm fw-bold text-white"
                    style={{
                      backgroundColor: "#EE2D41",
                      whiteSpace: "nowrap",
                    }}
                    disabled={createBatchMutation.isLoading}
                    onClick={handleSaveAndCreateBatch}
                  >
                    {createBatchMutation.isLoading
                      ? t("Creating...")
                      : t("Save & Create Batch")}
                  </button>
                  <span
                    className="fw-500 text-muted"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {selectedRows.length} {t("record selected")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(CreateBatchPage);
